/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-unstable-nested-components */
import * as _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactTable from 'react-table';

import { window } from '../../../globals';
import ProjectsModule from '../../../redux/projects';
import { getHumanReadableBytes } from '../../../utils';
import { Icon, icons } from '../../generic';
import Button from '../../generic/Button';
import Flex from '../../generic/Flex';
import TextInput from '../../generic/Forms/TextInput';
import { ICON_SIZE } from '../../generic/Icon/icon-constants';
import ProgressBar from '../../generic/ProgressBar';

import type { FileFieldUpdateDelegate, FileToUpload, FileToUploadAndStatus } from './UploadComponentPage';

type Props = {
  onRemoveFile(fileName: string): void;
  files: FileToUploadAndStatus[];
  onFileFieldUpdated: FileFieldUpdateDelegate;
  fetcher: 'sbom' | 'archive' | 'binary';
};

type CellProps = {
  original: FileToUploadAndStatus;
};

const FileNameCell = ({ original: row }: CellProps) => (
  <div className="margin-left-md">
    <Flex justify="flex-start">
      <div className="margin-right-lg">
        <Icon icon={icons.fileO} size={ICON_SIZE.LG} />
      </div>
      <div style={{ width: '100%', paddingRight: '30px' }}>
        <h5>{row.file.name}</h5>
        {
          row.status.bytesUploaded <= 0 && !row.status.errorMessage
            ? <p>{row.file.size ? getHumanReadableBytes(row.file.size) : '0 B'}</p>
            : <ProgressBar
                bytesFinished={row.status.bytesUploaded}
                bytesTotal={row.file.size ?? 0}
                hasFailed={!!row.status.errorMessage}
                errorMessage={row.status.errorMessage}
              />
        }
      </div>
    </Flex>
  </div>
);

const isRowDisabled = (row: FileToUploadAndStatus) => {
  return row.status.bytesUploaded > 0 && !row.status.errorMessage;
};

const doesFileLocatorExist = (name: string, fetcher: Props['fetcher'], existingArchives: ReturnType<typeof ProjectsModule.selectors.getProjectList>) => {
  const locator = `${fetcher}+${window.user?.organizationId}/${name}`;
  return existingArchives.some(archive => archive.locator === locator);
};

const nameHelperText = (file: FileToUpload, fetcher: Props['fetcher'], existingArchives: ReturnType<typeof ProjectsModule.selectors.getProjectList>) => {
  if (!file.customName || _.isEmpty(file.customName)) {
    return 'Required';
  }

  if (doesFileLocatorExist(file.customName, fetcher, existingArchives)) {
    return 'Adding to existing project';
  }

  return 'Creating a new project';
};

type ProjectNameCellProps = CellProps & {
  onFileFieldUpdated: FileFieldUpdateDelegate;
  fetcher: Props['fetcher'];
  existingArchives: ReturnType<typeof ProjectsModule.selectors.getProjectList>;
};

const ProjectNameCell = ({ original: row, onFileFieldUpdated, fetcher, existingArchives }: ProjectNameCellProps) => (
  <>
    <TextInput
      label={`${row.file.name}-customName`}
      value={row.file.customName || ''}
      isRequired
      onChange={(val: string) => {
        onFileFieldUpdated('customName', val, row.file);
      }}
      isDisabled={isRowDisabled(row)}
    />
    <p>{nameHelperText(row.file, fetcher, existingArchives)}</p>
  </>
);

type RevisionCellProps = CellProps & {
  onFileFieldUpdated: FileFieldUpdateDelegate;
};

const RevisionCell = ({ original: row, onFileFieldUpdated }: RevisionCellProps) => (
  <TextInput
    label={`${row.file.name}-revision`}
    value={row.file.revision || ''}
    placeholder="v1.2.3"
    onChange={(val: string) => {
      onFileFieldUpdated('revision', val, row.file);
    }}
    isDisabled={isRowDisabled(row)}
  />
);

type RemoveCellProps = CellProps & {
  onRemoveFile: Props['onRemoveFile'];
};

const RemoveCell = ({ original: row, onRemoveFile }: RemoveCellProps) => {
  if (row.status.bytesUploaded <= 0) {
    return (
      <div className="margin-top-xs">
        <Button
          icon={icons.trash}
          size="small"
          type="error"
          isDisabled={isRowDisabled(row)}
          onClick={() => onRemoveFile(row.file.name)}>
          Remove
        </Button>
      </div>
    );
  }
  return null;
};

const UploadComponentTable = ({ onRemoveFile, files, onFileFieldUpdated, fetcher }: Props) => {
  const dispatch = useDispatch();
  const existingArchives = useSelector(ProjectsModule.selectors.getProjectList);

  useEffect(() => {
    if (window.user) {
      dispatch(ProjectsModule.actions.fetchAll({
        organizationId: window.user.organizationId as number,
      }));
    }
  }, [dispatch]);

  const columns = useMemo(() => [
    {
      id: 'fileName',
      accessor: 'name',
      Header: 'File Name',
      Cell: FileNameCell,
    },
    {
      id: 'projectName',
      accessor: 'customName',
      Header: 'Project Name',
      Cell: (props: CellProps) => (
        <ProjectNameCell
          {...props}
          onFileFieldUpdated={onFileFieldUpdated}
          fetcher={fetcher}
          existingArchives={existingArchives}
        />
      ),
    },
    {
      id: 'revision',
      Header: 'Revision',
      Cell: (props: CellProps) => (
        <RevisionCell
          {...props}
          onFileFieldUpdated={onFileFieldUpdated}
        />
      ),
    },
    {
      id: 'remove',
      width: 100,
      Cell: (props: CellProps) => (
        <RemoveCell
          {...props}
          onRemoveFile={onRemoveFile}
        />
      ),
    },
  ], [onFileFieldUpdated, fetcher, existingArchives, onRemoveFile]);

  return (
    <ReactTable
      resizable={false}
      minRows={0}
      columns={columns}
      data={files}
      showPagination={false}
    />
  );
};

export default UploadComponentTable;
